import { useEffect, useState } from 'react';
import { Check, Loader2, X, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const TABS = [
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobadas' },
  { value: 'rejected', label: 'Rechazadas' },
];

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = (status = tab) => {
    setLoading(true);
    api
      .get(`/admin/requests?status=${status}`)
      .then(({ data }) => setRequests(data))
      .catch(() => toast.error('No se pudieron cargar las solicitudes'))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(tab), [tab]);

  const decide = async (requestId, status) => {
    setUpdating(requestId);
    try {
      await api.patch(`/admin/requests/${requestId}`, { status });
      toast.success(status === 'approved' ? 'Solicitud aprobada. Si el correo tiene cuenta, ya es socio.' : 'Solicitud rechazada.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar la solicitud.');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-requests-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Afiliaciones</p>
        <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <FileCheck className="h-7 w-7 text-cnib-blue" /> Solicitudes de membresía
        </h1>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            data-testid={`admin-requests-tab-${t.value}`}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${
              tab === t.value ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500" data-testid="admin-requests-empty">
          No hay solicitudes en este estado.
        </div>
      ) : (
        <div className="grid gap-4" data-testid="admin-requests-list">
          {requests.map((req) => (
            <div
              key={req.request_id}
              className="flex flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 md:flex-row md:items-center"
              data-testid={`admin-request-${req.request_id}`}
            >
              <div>
                <p className="font-heading font-bold text-slate-900">{req.full_name}</p>
                <p className="mt-0.5 text-sm text-slate-500">
                  {req.professional_type} · {req.city} {req.company ? `· ${req.company}` : ''}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {req.email} · {req.whatsapp} ·{' '}
                  {new Date(req.created_at).toLocaleDateString('es-BO', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
                {req.message && <p className="mt-2 text-sm italic text-slate-600">“{req.message}”</p>}
              </div>
              {req.status === 'pending' && (
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => decide(req.request_id, 'approved')}
                    disabled={updating === req.request_id}
                    data-testid="admin-request-approve-button"
                    className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" /> Aprobar
                  </button>
                  <button
                    onClick={() => decide(req.request_id, 'rejected')}
                    disabled={updating === req.request_id}
                    data-testid="admin-request-reject-button"
                    className="flex items-center gap-1.5 rounded-full bg-red-500/10 px-5 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-500/20 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" /> Rechazar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
